import {
	Box,
	Divider,
	Icon,
	IconButton,
	Paper,
	type PaperProps,
	Popover,
	Stack,
	type StackProps,
	Typography,
	type TypographyProps,
	useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { SxProps } from "@mui/system";
import React, { type JSXElementConstructor, useCallback, useEffect, useRef, useState } from "react";
import MenuIcon from "../../icons/react icons/material design/MenuIcon.tsx";

export type TSpacingPosition = "start" | "end" | "space-between" | "space-around";

type TPaddingVariantAll = "none" | "small" | "medium" | "large";
/**
 * Props for the ContentPaper component.
 * @category ui
 * @subcategory layout
 */
type SzContentPaperHeaderProps = {
	/** Children oh titleHeader */
	children?: React.ReactNode;
	/** Spacing between children
	 * @default "start"
	 */
	childrenSpacing?: TSpacingPosition;
	childrenFlexBasis?: string;
	/** Padding for gray box. */
	paddingVariant?: TPaddingVariantAll;
	/** Heading text */
	heading?: React.ReactNode;
	/** Subheading text */
	subHeading?: string;
	/** Subheading placement */
	subHeadingPlacement?: "end" | "bottom";
	subHeadingSx?: SxProps;
	subHeadingTypographyProps?: TypographyProps;
	headingTypographyProps?: TypographyProps;
	headingSx?: SxProps;
	/** If needed Icon can be added to the titleHeader */
	icon?: React.ReactElement<any, string | JSXElementConstructor<any>> | undefined;
	/** If needed Button can be added to the titleHeader */
	button?: React.ReactElement | undefined;
	/** Optional background color specification */
	bgColor?: string;
};

const paddings = {
	paddingAll: {
		none: 0,
		small: "0.25rem",
		medium: "0.5rem",
		large: "1rem",
	},
};

/**
 * Header component
 * If used, on the left side will appear titleHeader with optional icon
 * on the right side will stack up components from the right side.
 */

const ContentPaperHeader: React.FC<SzContentPaperHeaderProps> = (props) => {
	const {
		bgColor,
		children,
		icon,
		button,
		heading,
		childrenSpacing,
		paddingVariant,
		childrenFlexBasis,
		subHeading,
		subHeadingPlacement,
		subHeadingSx,
		subHeadingTypographyProps,
		headingTypographyProps,
		headingSx,
	} = props;

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

	const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const open = Boolean(anchorEl);

	return (
		<Stack
			direction="column"
			justifyContent="space-evenly"
			alignItems="stretch"
			sx={[
				(theme) => ({
					backgroundColor: bgColor ? bgColor : theme?.vars?.palette.grey[100],
					borderBottom: `1px solid ${theme?.vars?.palette.grey[300]}`,
					overflow: "hidden",
					padding: paddingVariant ? paddingVariant : 0,
				}),
				(theme) =>
					theme.applyStyles("dark", {
						backgroundColor: bgColor ? bgColor : theme?.vars?.palette.grey[900],
						borderBottom: `1px solid ${theme?.vars?.palette.grey[700]}`,
						overflow: "hidden",
						padding: paddingVariant ? paddingVariant : 0,
					}),
			]}
		>
			<Stack
				paddingLeft={"1rem"}
				paddingRight={"3rem"}
				paddingTop={"1rem"}
				paddingBottom={"1rem"}
				direction="row"
				justifyContent={heading || icon || button ? "space-between" : childrenSpacing}
				alignItems="center"
			>
				<Stack direction="row" justifyContent="start" alignItems="center" spacing={1} paddingLeft="0.5rem" flexGrow={1}>
					{button && <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>{button}</Box>}
					{icon && (
						<Icon
							fontSize="large"
							color="primary"
							sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
						>
							{icon}
						</Icon>
					)}
					{heading && (
						<Stack
							spacing={2}
							direction={subHeadingPlacement === "bottom" ? "column" : "row"}
							alignItems="center"
							justifyContent="space-between"
						>
							<Typography {...headingTypographyProps} sx={headingSx}>
								{heading}
							</Typography>
							{subHeading && (
								<Typography {...subHeadingTypographyProps} sx={subHeadingSx}>
									{subHeading}
								</Typography>
							)}
						</Stack>
					)}
					{isMobile && (
						<IconButton onClick={handleMenuClick}>
							<MenuIcon />
						</IconButton>
					)}
				</Stack>
				{!isMobile && children && (
					<Stack
						direction="row"
						justifyContent={heading || icon ? "flex-end" : childrenSpacing}
						alignItems="center"
						flexBasis={childrenFlexBasis}
					>
						{children}
					</Stack>
				)}
			</Stack>
			<Popover
				open={open}
				anchorEl={anchorEl}
				onClose={handleMenuClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
			>
				<Box sx={{ padding: "1rem" }}>
					<Stack spacing={2}>{children}</Stack>
				</Box>
			</Popover>
		</Stack>
	);
};

/**
 * Props for the ContentPaper component.
 * @category ui
 * @subcategory layout
 */
type SzContentPaperBodyProps = {
	/** Children element */
	children?: React.ReactNode;
	/** Spacing between children */
	childrenSpacing?: TSpacingPosition;
	/** Padding for body */
	paddingVariant?: TPaddingVariantAll;
	/**
	 * Overflow strategy for the body of the tile.
	 * @default "hidden",
	 */
	overflow?: "scrollAll" | "scrollX" | "scrollY" | "hidden";
} & StackProps;

/**
 * Body of paper, aligns given children vertically with possibility of adjusting spacing between components.
 */
const ContentPaperBody: React.FC<SzContentPaperBodyProps> = ({
	children,
	childrenSpacing = "start",
	paddingVariant = "none",
	sx: sxFromParent,
	overflow = "hidden",
	...stackProps
}) => {
	const ref = useRef<HTMLDivElement | null>(null);
	const [height, setHeight] = useState(0);

	useEffect(() => {
		if (ref.current && !!ref.current?.offsetHeight) {
			setHeight(ref.current.offsetHeight);
		}
	}, [children]);
	const overflowStrategy = (): React.CSSProperties => {
		switch (overflow) {
			case "hidden":
				return { overflow: "hidden" };
			case "scrollAll":
				return { overflow: "auto" };
			case "scrollX":
				return { overflowX: "auto" };
			case "scrollY":
				return { overflowY: "auto" };
			default:
				return {};
		}
	};

	const getOverflow = useCallback(overflowStrategy, []);

	return (
		<Stack
			ref={ref}
			{...stackProps}
			data-testid="content-paper-body"
			direction="column"
			justifyContent={childrenSpacing}
			alignItems="stretch"
			sx={{
				...(overflow && getOverflow()),
				...(paddingVariant && {
					padding: paddings.paddingAll[paddingVariant],
				}),
				...sxFromParent,
			}}
		>
			{children}
		</Stack>
	);
};

/**
 * Props for the ContentPaper component.
 * @category ui
 * @subcategory layout
 */
type SzContentPaperFooterProps = {
	/** Children of component */
	children?: React.ReactNode;
	/** Spacing between children horizontally default is space-between */
	childrenSpacing?: TSpacingPosition;
	/** If specified bg color is used */
	bgColor?: string;
};

/**
 * Footer of paper, contains given elements and aligns them according to your need.
 */
const ContentPaperFooter: React.FC<SzContentPaperFooterProps> = ({
	children,
	childrenSpacing = "space-between",
	bgColor,
}) => (
	<Stack
		direction="column"
		justifyContent="space-between"
		alignItems="stretch"
		sx={[
			(theme) => ({
				backgroundColor: bgColor ? bgColor : theme?.vars?.palette.grey[100],
			}),
			(theme) =>
				theme.applyStyles("dark", {
					backgroundColor: bgColor ? bgColor : theme?.vars?.palette.grey[900],
				}),
		]}
	>
		<Divider />
		<Stack direction="row" justifyContent={childrenSpacing} alignItems="stretch" spacing={2} padding={1}>
			{children}
		</Stack>
	</Stack>
);

/**
 * Props for the ContentPaper component.
 * @category ui
 * @subcategory layout
 */
type SzTilesBoxProps = {
	/** Height of the tile in absolute value. */
	height?: string;
	/** Min height of the tile in absolute value. */
	minHeight?: string;
	/** Max height of the tile in absolute value. */
	maxHeight?: string;
	/** Auto height of tile to the bottom of the window. When the window is resized, the tile change the height. */
	heightToBottomScreen?: boolean;
	bottomMargin?: number;
} & PaperProps;

/**
 * Subcomponents for the SzTileBox component.
 * @category ui
 * @subcategory layout
 */
type SzTilesBoxComponents = {
	/** Header of the tile. */
	Header: React.FC<SzContentPaperHeaderProps>;
	/** Body of the tile. */
	Body: React.FC<SzContentPaperBodyProps>;
	/** Footer of the tile. */
	Footer: React.FC<SzContentPaperFooterProps>;
};

/**
 * Tile component for the dashboard or others usage.
 * @param {SzTilesBoxProps} props - props
 * @param {string} props.height - height of the tile
 * @param {string} props.minHeight - min height of the tile
 * @param {string} props.maxHeight - max height of the tile
 * @param {boolean} props.heightToBottomScreen - auto height of tile to the bottom of the window
 * @param {number} props.bottomMargin - bottom margin
 * @component
 * @category ui
 * @subcategory layout
 */
const SzTilesBoxBase: React.FC<SzTilesBoxProps> = (props: SzTilesBoxProps) => {
	const {
		children,
		height,
		minHeight,
		maxHeight,
		bottomMargin = 30,
		heightToBottomScreen,
		sx: sxFromParent,
		...paperProps
	} = props;
	const [dynamicHeight, setDynamicHeight] = useState<string | number | undefined>(height);
	const elementRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const updateHeight = () => {
			if (heightToBottomScreen && elementRef.current) {
				const rect = elementRef.current.getBoundingClientRect();
				if (rect) {
					const heightFromTopToElement = rect.top;
					const windowHeight = window.innerHeight;
					const newHeight = windowHeight - heightFromTopToElement - bottomMargin;
					setDynamicHeight(newHeight);
				}
			}
		};

		updateHeight();
		window.addEventListener("resize", updateHeight);
		return () => window.removeEventListener("resize", updateHeight);
	}, [heightToBottomScreen, bottomMargin]);

	return (
		<Paper
			{...paperProps}
			ref={elementRef}
			data-testid="SzTilesBox"
			sx={{
				...sxFromParent,
				height: heightToBottomScreen ? dynamicHeight : height,
				minHeight: minHeight,
				maxHeight: maxHeight,
				display: "flex",
				flexDirection: "column",
			}}
		>
			{children}
		</Paper>
	);
};

type SzTilesBoxType = React.FC<SzTilesBoxProps> & SzTilesBoxComponents;

// Combine SzTilesBoxBase with the subcomponents and cast it to the combined type
const SzTilesBox: SzTilesBoxType = SzTilesBoxBase as SzTilesBoxType;
SzTilesBox.Header = ContentPaperHeader;
SzTilesBox.Body = ContentPaperBody;
SzTilesBox.Footer = ContentPaperFooter;

export default SzTilesBox;
